import type { MetricFormat } from "@hightouch/lib/ad-studio/insights-malloy/schemas/cube-meta";
import type { Reported } from "src/pages/ad-studio/insights/performance/types";

export function formatMetricValue(
  value: Reported,
  format: MetricFormat,
): string {
  if (value === "not-reported") {
    return "—";
  }
  switch (format) {
    case "currency-compact":
      return formatCurrency(value);
    case "currency-precise":
      return `$${value.toFixed(2)}`;
    case "number-compact":
      return formatCompactNumber(value);
    case "percent":
      return `${value.toFixed(2)}%`;
    case "frequency":
      return `${value.toFixed(2)}x`;
    default:
      // Exhaustiveness check — TS errors here if a new MetricFormat variant
      // is added without a case. Runtime falls through to String() if the
      // type system is ever bypassed (e.g. bad cast in a test).
      format satisfies never;
      return String(value);
  }
}

export const noAdsMessage = (hasMultipleAdAccounts: boolean): string =>
  `Try adjusting your filters${
    hasMultipleAdAccounts
      ? " and make sure you have the correct ad account selected up top"
      : ""
  }`;

/** Compact USD, e.g. "$1.5M" / "$1.5K" / "$500". */
export function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

/** Compact USD, or a dash when the value is missing. */
export const formatCurrencyOrDash = (value: number | null): string =>
  value === null ? "—" : formatCurrency(value);

/** One decimal place, or a dash when the value is missing. */
export const formatPercentOrDash = (value: number | null): string =>
  value === null ? "—" : `${value.toFixed(1)}%`;

function formatCompactNumber(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
  return value.toFixed(0);
}

/** Format a delta value as a string with sign */
export function formatDelta(delta: number | null): string {
  if (delta === null) return "—";
  const sign = delta > 0 ? "+" : "";
  return `${sign}${delta.toFixed(1)}%`;
}
